import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { Input } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { showToast, ToastType } from '@/helpers/toast';
import { PlayIcon } from '@/resources';
import {
  useCreatePlayerReleaseMutation,
  useGetPlayerReleaseByIdQuery,
  useUpdatePlayerReleaseMutation
} from '@/services/playerRelease.service';
import { CreateEditPlayerReleaseProps } from '@/types/PlayerReleaseTypes';

import { createReleaseScheme, updateReleaseScheme } from './Validation';

export const CreateEditPlayerRelease = () => {
  const [t] = useTranslation('common');
  const { playerReleaseId: id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isEditPage = !!id;
  const [error, setError] = useState<number>();
  const label = isEditPage ? t('editPlayerRelease') : t('addPlayerRelease');

  const { data: playerRelease, isLoading: isLoadingRelease } = useGetPlayerReleaseByIdQuery(
    Number(id),
    {
      onSuccess: () => undefined,
      onError: (error) => {
        setError(error.status);
      }
    }
  );

  const getInitialValues = () => {
    return {
      id: playerRelease?.data.id,
      url: playerRelease?.data.url || '',
      version: playerRelease?.data.version || '',
      isPublic: playerRelease?.data.isPublic || false
    };
  };

  const redirectToIndex = () => navigate('/player-releases');

  const { mutate: createPlayerRelease } = useCreatePlayerReleaseMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('playerReleaseCreated'));
      redirectToIndex();
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      setIsSubmitting(false);
    }
  });

  const { mutate: updatePlayerRelease } = useUpdatePlayerReleaseMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('playerReleaseUpdated'));
      redirectToIndex();
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      setIsSubmitting(false);
    }
  });

  const handleSubmit = (values: CreateEditPlayerReleaseProps) => {
    setIsSubmitting(true);

    const payloadPlayerRelease = {
      id: values.id,
      version: values.version,
      url: values.url,
      isPublic: values.isPublic
    };

    if (isEditPage) {
      updatePlayerRelease(payloadPlayerRelease);
    } else {
      createPlayerRelease(payloadPlayerRelease);
    }
  };

  return (
    <div className='flex h-full justify-center'>
      {(isEditPage && isLoadingRelease) || isSubmitting ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='mx-3 mt-7 flex w-full flex-col md:mx-20'>
          <div className='w-full'>
            <Breadcrumb
              icon={<PlayIcon className='h-4 stroke-[0.5] text-lg text-primary-blue' />}
              iconPath={'/player-releases'}
              path={[{ label: label }]}
            />
          </div>
          <div className='mt-9 flex flex-col'>
            <Label text={label} type={LABEL_TYPE.H1} />
            <Label text={t('generalInformation')} type={LABEL_TYPE.H3_BOLD} className='my-9' />
            <Formik
              enableReinitialize
              validationSchema={isEditPage ? updateReleaseScheme : createReleaseScheme}
              initialValues={getInitialValues()}
              onSubmit={handleSubmit}>
              {({ errors, touched, isValid, dirty }) => (
                <Form id='CreateEdit'>
                  <div className='flex flex-col'>
                    <div className='grid w-full grid-cols-2 gap-x-9 gap-y-3'>
                      <Input
                        id='version'
                        label={t('version')}
                        type='text'
                        isRequired
                        width={'w-full'}
                        errorMessage={touched.version ? (errors.version as string) : undefined}
                        isInvalid={!!(errors.version && touched.version)}
                      />
                      <Input
                        id='url'
                        label={t('url')}
                        type='text'
                        isRequired
                        width={'w-full'}
                        errorMessage={touched.url ? (errors.url as string) : undefined}
                        isInvalid={!!(errors.url && touched.url)}
                      />
                    </div>
                  </div>
                  <div className='mt-10 flex w-full justify-end'>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      type='submit'
                      disabled={isSubmitting || !(isValid && dirty)}>
                      {isEditPage ? t('saveChanges') : t('addPlayerRelease')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};
